import settings from "../settings";
import axios from "axios";
import vueapp from "../main";
import moment from "moment";

const adjustBarPositions = (data) => {
    data.sort((a, b) => a.x - b.x || a.x2 - b.x2);

    for (let i = 0; i < data.length; i++) {
        let currentBar = data[i];
        for (let j = 0; j < i; j++) {
            let previousBar = data[j];

            if (
                currentBar.x < previousBar.x2 &&
                currentBar.x2 > previousBar.x &&
                currentBar.y === previousBar.y
            ) {
                currentBar.y += 0.27;
            }
        }
    }

    return data;
};
const getColorForLine = (lineName) => {
    if (lineName == "waffle_line_1") return "#0029ff";
    else if (lineName == "waffle_line_2") return "#a2b1ff";
    else if (lineName == "waffle_line_3") return "#d0d8ff";
    else if (lineName == "waffle_line_4") return "#e1e6fd";
    else if (lineName == "toaster_1") return "#146c00";
    else if (lineName == "toaster_2") return "#91ff76";
    else if (lineName == "polaris_3") return "#de00ff";
    else if (lineName == "polaris_1") return "#f0a1ff";
    return "#ff7300";
};

const translateText = (lineName) => {
    return vueapp.t(lineName);
};

let filters = {
    periodRange: [],
    categoryIds: [],
    brandIds: [],
    productCodes: [],
    netoWeights: [],
    productProductionTypes: [],
    lineNumbers: [],
    operatorNames: [],
    lineWorkerNames: [],
    shifts: [],
};

let defaultChartOptions = {
    boost: {
        enabled: true,
        useGPUTranslations: true,
        usePreallocated: true,
    },
    chart: {
        type: "column",
        marginTop: 50,
    },
    title: {
        text: "",
    },
    xAxis: {
        categories: [],
    },
    yAxis: [],
    series: [],
};

let defaultTimelineChartOptions = {
    boost: {
        enabled: true,
        useGPUTranslations: true,
        usePreallocated: true,
    },
    chart: {
        type: "xrange",
        marginTop: 50,
    },
    title: {
        text: "",
    },
    xAxis: {
        type: "datetime",
        title: {text: "Aeg"},
        plotLines: [
            {
                color: "black",
                width: 2,
                value: Date.now(),
                label: {text: "Praegune aeg", align: "right", y: -10},
            },
        ],
    },
    yAxis: {
        categories: [],
        reversed: true,
        title: {text: "Tootmisliin"},
    },
    series: [],
};

let initialState = {
    groupByPeriod: "year-month",
    filters: {...filters},
    chartOptions: {...defaultChartOptions},
    timelineChartOptions: {...defaultTimelineChartOptions},
    headers: [],
    workOrders: [],
    filtersVisible: true,
    dataLoading: false,
    showSkeleton: false,
    firstLoadingDone: false,
    errorMsg: null,
    requestError: false,
    datesChanged: true,
    filterDates: {},
    fetchDates: {},
};

export default {
    namespaced: true,
    state: {...initialState},
    actions: {
        async fetchData({state, commit}) {
            commit("setShowSkeleton", true);
            commit("setFirstLoadingDone", true);
            commit("setDataLoading", true);
            commit("setDatesChanged", false);
            commit("resetChart");
            commit("resetTimelineChart");
            let filters = {...state.filters};

            if (state.filterDates?.start && state.filterDates?.end) {
                const startYear = state.filterDates.start.getFullYear();
                const startMonth = (state.filterDates.start.getMonth() + 1).toString().padStart(2, "0");
                const startDay = state.filterDates.start.getDate().toString().padStart(2, "0");
                const endYear = state.filterDates.end.getFullYear();
                const endMonth = (state.filterDates.end.getMonth() + 1).toString().padStart(2, "0");
                const endDay = state.filterDates.end.getDate().toString().padStart(2, "0");
                const dateData = {
                    start: `${startYear}-${startMonth}-${startDay}`,
                    end: `${endYear}-${endMonth}-${endDay}`,
                };
                filters.periodRange = dateData;
                commit("setFetchDates", dateData);
            }

            filters.groupByPeriod = state.groupByPeriod;

            try {
                let result = await axios.post(settings.api_url + "/production-analysis-data/", filters);

                if (result && result.data && result.data.success === 1) {
                    let workOrders = result.data.work_orders;
                    commit("setWorkOrders", workOrders);
                    commit("setShowSkeleton", false);
                } else {
                    let data = result.data;
                    let errorMsg = data.msg || "Error";
                    commit("setErrorMessage", errorMsg);
                    commit("setRequestError", true);
                    commit("setShowSkeleton", true);
                }
            } catch (e) {
                console.log("- ANALYSIS DATA FETCH ERROR -");
                console.log(e);
            } finally {
                commit("setDataLoading", false);
            }
        },
        generateChartData({state, commit, getters}) {
            commit("resetChart");
            const workOrders = getters.filteredWorkOrders().filter(wo => wo.status === "confirmed");
            const lineNumbers = new Set();

            if (!workOrders || workOrders.length === 0) {
                return {
                    ...state.chartOptions,
                    series: [],
                };
            }

            const monthNames = [
                "Jaan",
                "Veeb",
                "Mär",
                "Apr",
                "Mai",
                "Juuni",
                "Juuli",
                "Aug",
                "Sep",
                "Okt",
                "Nov",
                "Dets",
            ];
            const groupedData = {};
            const groupKeyMap = {
                "year-month": "group_by_key_month",
                "year-week": "group_by_key_week",
                "year-day": "planned_production_date"
            }
            workOrders.forEach((order) => {
                const index = order[groupKeyMap[state.groupByPeriod]] || null

                if (!groupedData[index]) {
                    groupedData[index] = {
                        totalWeight: 0,
                        kgPerHour: 0,
                        totalMinutes: 0,
                        lineData: {},
                    };
                }

                // Track unique line numbers
                lineNumbers.add(order.line_number);

                // Group by line_number within each period
                if (!groupedData[index].lineData[order.line_number]) {
                    groupedData[index].lineData[order.line_number] = {
                        totalWeight: 0,
                        totalMinutes: 0,
                    };
                }

                // Add data to both total and line-specific groups
                groupedData[index].totalWeight += order.product_produced_total_neto_weight_kg;
                groupedData[index].totalMinutes += order.actual_production_time_in_minutes;
                groupedData[index].lineData[order.line_number].totalWeight +=
                    order.product_produced_total_neto_weight_kg;
                groupedData[index].lineData[order.line_number].totalMinutes += order.actual_production_time_in_minutes;
            });

            Object.keys(groupedData).forEach((index) => {
                const data = groupedData[index];
                let kgPerHour = data.totalMinutes > 0 ? data.totalWeight / (data.totalMinutes / 60) : 0;
                let totalWeight = data.totalWeight;
                data.kgPerHour = Math.round(kgPerHour * 100) / 100;
                data.totalWeight = Math.round(totalWeight * 100) / 100;
            });

            const xAxisValues = getters.xAxisValues;
            const xAxisCategories = xAxisValues.map((dateStr) => {
                if (state.groupByPeriod.includes("week")) {
                    const [year, week] = dateStr.split("-");
                    return `${week.replace("W", "N")} ${year}`;
                } else if (state.groupByPeriod.includes("day")) {
                    const [year, month, day] = dateStr.split("-");
                    return `${day} ${monthNames[parseInt(month, 10) - 1]} ${year}`;
                } else {
                    const [year, month] = dateStr.split("-");
                    return `${monthNames[parseInt(month, 10) - 1]} ${year.slice(-2)}`;
                }
            });

            const lineSeriesArray = Array.from(lineNumbers)
                .filter((lineNumber) => lineNumber) // Filter out null/undefined
                .map((lineNumber) => {
                    return {
                        name: translateText(
                            `${lineNumber.charAt(0).toUpperCase() + lineNumber.slice(1).replaceAll("_", " ")}`
                        ),
                        data: xAxisValues.map(
                            (key) => Math.round(groupedData[key]?.lineData[lineNumber]?.totalWeight * 100) / 100 || 0
                        ),
                        type: "column",
                        stack: "production",
                        yAxis: 0,
                        dataLabels: {enabled: false},
                        tooltip: {valueSuffix: " kg"},
                        originalLine: lineNumber,
                        color: getColorForLine(lineNumber),
                    };
                });

            // Sort the series: waffles first, then polaris, then toasters
            lineSeriesArray.sort((a, b) => {
                const lineA = a.originalLine.toLowerCase();
                const lineB = b.originalLine.toLowerCase();

                // Waffle lines first
                if (lineA.includes("waffle") && !lineB.includes("waffle")) return -1;
                if (!lineA.includes("waffle") && lineB.includes("waffle")) return 1;

                // Polaris lines second
                if (lineA.includes("polaris") && !lineB.includes("polaris") && !lineB.includes("waffle")) return -1;
                if (!lineA.includes("polaris") && lineB.includes("polaris") && !lineA.includes("waffle")) return 1;

                // Toaster lines last
                if (lineA.includes("toaster") && !lineB.includes("toaster")) return 1;
                if (!lineA.includes("toaster") && lineB.includes("toaster")) return -1;

                // If they're the same type, sort by number
                const numA = parseInt(lineA.replace(/\D/g, "")) || 0;
                const numB = parseInt(lineB.replace(/\D/g, "")) || 0;
                return numA - numB;
            });

            // Add kg/h line series
            const kgPerHourSeries = {
                name: "kg/h",
                data: xAxisValues.map((key) => groupedData[key]?.kgPerHour || 0),
                type: "line",
                color: "#f41823",
                yAxis: 1,
                tooltip: {valueSuffix: " kg/h"},
                dataLabels: {enabled: true, format: "{y} kg/h"},
                marker: {
                    enabled: true,
                    radius: 6,
                    lineWidth: 2,
                    lineColor: "rgba(216, 35, 50, 1)",
                    fillColor: "#ffffff",
                    symbol: "circle",
                },
            };

            const maxTotalWeight = Math.max(...xAxisValues.map((key) => groupedData[key]?.totalWeight || 0));

            const allSeries = [...lineSeriesArray, kgPerHourSeries].filter((series) => series !== null);

            const d = {
                ...state.chartOptions,
                tooltip: {
                    useHTML: true,
                    backgroundColor: "rgba(30, 30, 30, 1)",
                    borderRadius: 8,
                    borderWidth: 0,
                    formatter: function () {
                        const color = this.color;
                        const name = this.series.name;
                        const value = this.y;

                        const formattedValue = value.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, " ");

                        const formattedLabel = `${formattedValue} kg`;

                        return `
                            <div style="display: flex; align-items: center;">
                                <div style="width: 24px; display: flex; align-items: center; justify-content: center;">
                                    <div style="width: 12px; height: 12px; background-color: ${color}; border-radius: 50%;"></div>
                                </div>
                                <div style="display: flex; flex-direction: column;">
                                    <span style="color: rgba(141, 153, 146, 1); font-size: 11px;">${name}</span>
                                    <span style="color: white; font-size: 14px; font-weight: 600;">${formattedLabel}</span>
                                </div>
                            </div>
                        `;
                    },
                },
                xAxis: {
                    ...state.chartOptions.xAxis,
                    categories: xAxisCategories,
                },
                yAxis: [
                    {
                        labels: {
                            formatter: function () {
                                return `${this.value} kg`;
                            },
                        },
                        title: {text: "Kg toodetud"},
                        opposite: false,
                        plotLines: [
                            {
                                value: maxTotalWeight,
                                width: 2,
                                dashStyle: "dash",
                                label: {
                                    text: `${maxTotalWeight} kg`,
                                    align: "right",
                                },
                            },
                        ],
                        stackLabels: {
                            enabled: true,
                            style: {
                                fontWeight: "bold",
                                color: "gray",
                            },
                        },
                    },
                    {title: {text: "kg/h"}, opposite: true},
                ],
                plotOptions: {
                    column: {
                        stacking: "normal",
                    },
                },
                series: allSeries,
            };

            commit("setChartOptions", d)
        },
        generateTimelineChartData({state, commit, getters}) {
            commit("resetTimelineChart");
            const workOrders = getters.filteredWorkOrders();
            if (!workOrders || workOrders.length === 0) return {...state.timelineChartOptions, series: []};
            const {start, end} = state.fetchDates;
            if (!start || !end) return {...state.timelineChartOptions, series: []};

            const lineNumbers = [...new Set(workOrders.map((w) => w.line_number).filter(Boolean))];
            const seriesMap = {};

            lineNumbers.forEach((line) => {
                seriesMap[line] = {
                    name: translateText(`${line.charAt(0).toUpperCase() + line.slice(1).replaceAll("_", " ")}`),
                    data: [],
                    type: "xrange",
                    pointWidth: 12,
                };
                /*seriesMap[`est-${line}`] = {
                    name:
                        translateText(`${line.charAt(0).toUpperCase() + line.slice(1).replaceAll("_", " ")}`) +
                        " (plan)",
                    data: [],
                    type: "xrange",
                    pointWidth: 4,
                };*/
            });

            workOrders.forEach((order) => {
                if (!order.start_time || !order.end_time || !order.line_number) return;

                const startDateTime = `${order.planned_production_date} ${order.start_time}:00`;
                const endDateTime = `${order.planned_production_date} ${order.end_time}:00`;

                const start = new Date(startDateTime).getTime();
                let end = new Date(endDateTime).getTime();

                if (isNaN(start) || isNaN(end)) return;

                if (start > end) {
                    const updatedEnd = moment(end).add(1, 'days');
                    end = updatedEnd.valueOf();
                }
                const yIndex = lineNumbers.indexOf(order.line_number);

                seriesMap[order.line_number].data.push({
                    x: start,
                    x2: end,
                    y: yIndex - 0.1,
                    work_order_id: order.id,
                    production_order_number: order.production_order_number,
                    actual_quantity: order.actual_quantity,
                    planned_quantity: order.planned_quantity,
                    type: "actual",
                    color:
                        order.status === "plan"
                            ? "gray"
                            : order.status === "in_progress"
                                ? "blue"
                                : order.status === "done"
                                    ? "green"
                                    : order.status === "confirmed"
                                        ? "purple"
                                        : order.status === "cancelled"
                                            ? "red"
                                            : "gray",
                });

                /*const estParts = order.estimated_time?.split(":");
                if (estParts?.length === 2) {
                    const estMinutes = parseInt(estParts[0], 10);
                    const estSeconds = parseInt(estParts[1], 10);
                    const estMs = (estMinutes * 60 + estSeconds) * 1000;

                    seriesMap[`est-${order.line_number}`].data.push({
                        x: start,
                        x2: start + estMs,
                        y: yIndex + 0.1,
                        work_order_id: order.id,
                        production_order_number: order.production_order_number,
                        actual_quantity: order.actual_quantity,
                        planned_quantity: order.planned_quantity,
                        type: "planned",
                        color:
                            order.status === "plan"
                                ? "gray"
                                : order.status === "in_progress"
                                ? "blue"
                                : order.status === "done"
                                ? "green"
                                : order.status === "confirmed"
                                ? "purple"
                                : order.status === "cancelled"
                                ? "red"
                                : "gray",
                    });
                }*/
            });
            Object.keys(seriesMap).forEach((key) => {
                seriesMap[key].data = adjustBarPositions(seriesMap[key].data);
            })
            const minTime = new Date(`${start} 00:00:00`).getTime();
            const maxTime = new Date(`${end} 23:59:59`).getTime();
            const totalRangeDays = (maxTime - minTime) / (24 * 3600 * 1000);
            const pixelsPerDay = 200;
            const minWidth = totalRangeDays * pixelsPerDay;

            const d = {
                ...state.timelineChartOptions,
                chart: {
                    type: "xrange",
                    height: 350,
                    scrollablePlotArea: {
                        minWidth: minWidth,
                        scrollPositionX: 1,
                    },
                    zoomType: "x",
                    panning: true,
                    panKey: "shift",

                    resetZoomButton: {
                        position: {
                            align: "right",
                            verticalAlign: "top",
                            x: -10,
                            y: 10
                        },
                        relativeTo: "chart",
                        theme: {
                            fill: "#f7f7f7",
                            stroke: "#999999",
                            r: 4,
                            states: {
                                hover: {
                                    fill: "#e6e6e6",
                                    stroke: "#666666"
                                }
                            },
                            style: {
                                fontWeight: "bold",
                                fontSize: "12px"
                            }
                        }
                    }
                },
                plotOptions: {
                    xrange: {
                        grouping: false,
                        pointPadding: 0.05,
                        groupPadding: 0.05,
                        borderWidth: 0,
                        pointWidth: 12,
                        borderRadius: 3,
                        colorByPoint: false,
                        states: {
                            hover: {
                                brightness: 0.1,
                                borderWidth: 1
                            }
                        },
                        dataLabels: {
                            enabled: false,
                            style: {
                                fontSize: "10px"
                            }
                        }
                    }
                },
                xAxis: {
                    type: "datetime",
                    min: minTime,
                    max: maxTime,
                    scrollbar: {
                        enabled: true,
                    },
                    tickInterval: 24 * 3600 * 1000,
                    title: {
                        text: "Time",
                    },
                },
                yAxis: {
                    categories: lineNumbers.map((line) =>
                        translateText(`${line.charAt(0).toUpperCase() + line.slice(1).replaceAll("_", " ")}`)
                    ),
                    reversed: true,
                    title: {text: translateText("Production Line")},
                    min: 0,
                    max: lineNumbers.length - 1,
                    startOnTick: true,
                    endOnTick: true,
                    tickLength: 0,
                    lineWidth: 1,
                    offset: 0,
                    labels: {
                        padding: 2
                    }
                },
                series: Object.values(seriesMap),
                tooltip: {
                    formatter: function () {
                        const startDate = new Date(this.point.x);
                        const endDate = new Date(this.point.x2);

                        const formatDate = (date) => {
                            const day = date.getDate().toString().padStart(2, "0");
                            const month = (date.getMonth() + 1).toString().padStart(2, "0");
                            const hours = date.getHours().toString().padStart(2, "0");
                            const minutes = date.getMinutes().toString().padStart(2, "0");
                            return `${day}.${month} ${hours}:${minutes}`;
                        };

                        const isPlanned = this.point.type === "planned";

                        let tooltip = `
                    <b>${translateText(this.series.name)} - ${this.point.production_order_number || ''}</b><br/>
                    ${formatDate(startDate)} - ${formatDate(endDate)}<br/>
                    ${translateText("Planned quantity")}: ${this.point.planned_quantity || ''}<br/>
                `;

                        if (!isPlanned && this.point.actual_quantity) {
                            tooltip += `${translateText("Produced quantity")}: ${this.point.actual_quantity || ''}<br/>`;
                        }

                        return tooltip;
                    },
                },
            };

            commit("setTimelineChartOptions", d)
        }
    },
    mutations: {
        setGroupByPeriod(state, value) {
            state.groupByPeriod = value;
        },
        updateFilters(state, {filter, value}) {
            state.filters = {
                ...state.filters,
                [filter]: value,
            };
        },
        resetFilters(state) {
            state.filters = filters;
        },
        setDatesChanged(state, bool) {
            state.datesChanged = bool;
        },
        toggleFilterVisible(state) {
            state.filtersVisible = !state.filtersVisible;
        },
        setErrorMessage(state, errorMsg) {
            state.errorMsg = errorMsg;
        },
        setRequestError(state, bool) {
            state.requestError = bool;
        },
        setWorkOrders(state, work_orders) {
            state.workOrders = work_orders;
        },
        setDataLoading(state, bool) {
            state.dataLoading = bool;
        },
        setShowSkeleton(state, bool) {
            state.showSkeleton = bool;
        },
        setFirstLoadingDone(state, bool) {
            state.firstLoadingDone = bool;
        },
        resetChart(state) {
            state.chartOptions = defaultChartOptions;
        },
        resetTimelineChart(state) {
            state.timelineChartOptions = defaultTimelineChartOptions;
        },
        setFetchDates(state, data) {
            state.fetchDates = data;
        },
        setFilterDates(state, data) {
            state.filterDates = data;
        },
        setChartOptions(state, data) {
            state.chartOptions = data
        },
        setTimelineChartOptions(state, data) {
            state.timelineChartOptions = data
        },
        resetView(state) {
            Object.assign(state, initialState)
        }
    },
    getters: {
        filteredWorkOrders:
            (state) =>
                (excludeFilters = []) => {
                    let filtered = [...state.workOrders];
                    if (
                        !excludeFilters.includes("categories") &&
                        state.filters.categoryIds &&
                        state.filters.categoryIds.length > 0
                    ) {
                        filtered = filtered.filter((work_order) =>
                            state.filters.categoryIds.includes(work_order.product_category_id)
                        );
                    }

                    if (!excludeFilters.includes("brands") && state.filters.brandIds && state.filters.brandIds.length > 0) {
                        filtered = filtered.filter((work_order) =>
                            state.filters.brandIds.includes(work_order.product_brand_id)
                        );
                    }

                    if (
                        !excludeFilters.includes("products") &&
                        state.filters.productCodes &&
                        state.filters.productCodes.length > 0
                    ) {
                        filtered = filtered.filter((work_order) =>
                            state.filters.productCodes.includes(work_order.product_code)
                        );
                    }

                    if (
                        !excludeFilters.includes("neto_weights") &&
                        state.filters.netoWeights &&
                        state.filters.netoWeights.length > 0
                    ) {
                        filtered = filtered.filter((work_order) =>
                            state.filters.netoWeights.includes(work_order.product_neto_weight)
                        );
                    }

                    if (
                        !excludeFilters.includes("line_numbers") &&
                        state.filters.lineNumbers &&
                        state.filters.lineNumbers.length > 0
                    ) {
                        filtered = filtered.filter((work_order) =>
                            state.filters.lineNumbers.includes(work_order.line_number)
                        );
                    }

                    if (
                        !excludeFilters.includes("operators") &&
                        state.filters.operatorNames &&
                        state.filters.operatorNames.length > 0
                    ) {
                        filtered = filtered.filter((work_order) =>
                            work_order.operators.some((op) => state.filters.operatorNames.includes(op.name))
                        );
                    }

                    if (
                        !excludeFilters.includes("line_workers") &&
                        state.filters.lineWorkerNames &&
                        state.filters.lineWorkerNames.length > 0
                    ) {
                        filtered = filtered.filter((work_order) =>
                            work_order.line_workers.some((lw) => state.filters.lineWorkerNames.includes(lw.name))
                        );
                    }

                    if (
                        !excludeFilters.includes("product_production_type") &&
                        state.filters.productProductionTypes &&
                        state.filters.productProductionTypes.length > 0
                    ) {
                        filtered = filtered.filter((work_order) =>
                            state.filters.productProductionTypes.includes(work_order.product_production_type)
                        );
                    }

                    if (!excludeFilters.includes("shifts") && state.filters.shifts && state.filters.shifts.length > 0) {
                        filtered = filtered.filter((work_order) => state.filters.shifts.includes(work_order.shift_type));
                    }
                    return filtered;
                },
        productOptions(state, getters) {
            const allWorkOrders = state.workOrders;
            const activeWorkOrders = getters.filteredWorkOrders(["products"]);
            if (!allWorkOrders || allWorkOrders.length === 0) return [];

            const activeProductCodes = new Set(activeWorkOrders.map((work_order) => work_order.product_code));

            const productMap = new Map();
            const activeProducts = [];
            const inactiveProducts = [];

            allWorkOrders.forEach((work_order) => {
                const productValue = work_order.product_code;
                const productText = `[${work_order.product_code}] ${work_order.product_name}`;

                if (!productValue || !productText || productMap.has(productValue)) {
                    return;
                }

                const isActive = activeProductCodes.has(productValue);
                const productOption = {
                    text: productText,
                    value: productValue,
                    active: isActive,
                };

                if (isActive) {
                    activeProducts.push(productOption);
                } else {
                    inactiveProducts.push(productOption);
                }

                productMap.set(productValue, productOption);
            });

            return [...activeProducts, ...inactiveProducts];
        },
        categoryOptions(state, getters) {
            const allWorkOrders = state.workOrders;
            const activeWorkOrders = getters.filteredWorkOrders(["products", "brands", "categories"]);
            if (!allWorkOrders || allWorkOrders.length === 0) return [];

            const activeCategoryIds = new Set(activeWorkOrders.map((work_order) => work_order.product_category_id));

            const categoryMap = new Map();
            const activeCategories = [];
            const inactiveCategories = [];

            allWorkOrders.forEach((work_order) => {
                const categoryValue = work_order.product_category_id;
                const categoryText = work_order.product_category_name;

                if (!categoryValue || !categoryText || categoryMap.has(categoryValue)) {
                    return;
                }

                const isActive = activeCategoryIds.has(categoryValue);
                const categoryOption = {
                    text: categoryText,
                    value: categoryValue,
                    active: isActive,
                };

                if (isActive) {
                    activeCategories.push(categoryOption);
                } else {
                    inactiveCategories.push(categoryOption);
                }

                categoryMap.set(categoryValue, categoryOption);
            });

            return [...activeCategories, ...inactiveCategories];
        },
        brandOptions(state, getters) {
            const allWorkOrders = state.workOrders;
            const activeWorkOrders = getters.filteredWorkOrders(["products", "brands"]);
            if (!allWorkOrders || allWorkOrders.length === 0) return [];

            const activeBrandIds = new Set(activeWorkOrders.map((work_order) => work_order.product_brand_id));

            const brandMap = new Map();
            const activeBrands = [];
            const inactiveBrands = [];

            allWorkOrders.forEach((work_order) => {
                const brandValue = work_order.product_brand_id;
                const brandText = work_order.product_brand_name;

                if (!brandValue || !brandText || brandMap.has(brandValue)) {
                    return;
                }

                const isActive = activeBrandIds.has(brandValue);
                const brandOption = {
                    text: brandText,
                    value: brandValue,
                    active: isActive,
                };

                if (isActive) {
                    activeBrands.push(brandOption);
                } else {
                    inactiveBrands.push(brandOption);
                }

                brandMap.set(brandValue, brandOption);
            });

            return [...activeBrands, ...inactiveBrands];
        },
        netoWeightOptions(state, getters) {
            const allWorkOrders = state.workOrders;
            const activeWorkOrders = getters.filteredWorkOrders(["neto_weights"]);
            if (!allWorkOrders || allWorkOrders.length === 0) return [];

            const activeWeights = new Set(
                activeWorkOrders.map((work_order) => work_order.product_neto_weight).filter((weight) => weight != null)
            );

            const weightMap = new Map();
            const activeWeightOptions = [];
            const inactiveWeightOptions = [];

            allWorkOrders.forEach((work_order) => {
                const weightValue = work_order.product_neto_weight;

                if (weightValue == null || weightMap.has(weightValue)) {
                    return;
                }

                const weightText = `${weightValue} g`;
                const isActive = activeWeights.has(weightValue);
                const weightOption = {
                    text: weightText,
                    value: weightValue,
                    active: isActive,
                };
                if (isActive) {
                    activeWeightOptions.push(weightOption);
                } else {
                    inactiveWeightOptions.push(weightOption);
                }

                weightMap.set(weightValue, weightOption);
            });

            return [...activeWeightOptions, ...inactiveWeightOptions];
        },
        lineNumberOptions(state, getters) {
            const allWorkOrders = state.workOrders;
            const activeWorkOrders = getters.filteredWorkOrders(["line_numbers"]);
            if (!allWorkOrders || allWorkOrders.length === 0) return [];

            const activeLineNumbers = new Set(activeWorkOrders.map((work_order) => work_order.line_number));

            const lineNumberMap = new Map();
            const activeLineNumberOptions = [];
            const inactiveLineNumberOptions = [];

            allWorkOrders.forEach((work_order) => {
                const lineNumberValue = work_order.line_number;

                if (!lineNumberValue || lineNumberMap.has(lineNumberValue)) {
                    return;
                }

                const isActive = activeLineNumbers.has(lineNumberValue);
                const lineNumberOption = {
                    text: (lineNumberValue.charAt(0).toUpperCase() + lineNumberValue.slice(1)).replaceAll("_", " "),
                    value: lineNumberValue,
                    active: isActive,
                };

                if (isActive) {
                    activeLineNumberOptions.push(lineNumberOption);
                } else {
                    inactiveLineNumberOptions.push(lineNumberOption);
                }

                lineNumberMap.set(lineNumberValue, lineNumberOption);
            });

            return [...activeLineNumberOptions, ...inactiveLineNumberOptions];
        },
        operatorOptions(state, getters) {
            const allWorkOrders = state.workOrders;
            const activeWorkOrders = getters.filteredWorkOrders(["operators"]);
            if (!allWorkOrders || allWorkOrders.length === 0) return [];

            const activeOperators = new Set(
                activeWorkOrders.flatMap((work_order) => work_order.operators.map((op) => op.name))
            );

            const operatorsMap = new Set();
            const activeOperatorOptions = [];
            const inactiveOperatorOptions = [];

            allWorkOrders.forEach((work_order) => {
                if (!work_order.operators) return;

                work_order.operators.forEach((operator) => {
                    if (!operator.name || operatorsMap.has(operator.name)) return;

                    operatorsMap.add(operator.name);
                    const isActive = activeOperators.has(operator.name);
                    const operatorOption = {
                        text: operator.name,
                        value: operator.name,
                        active: isActive,
                    };

                    if (isActive) {
                        activeOperatorOptions.push(operatorOption);
                    } else {
                        inactiveOperatorOptions.push(operatorOption);
                    }
                });
            });

            return [...activeOperatorOptions, ...inactiveOperatorOptions];
        },
        lineWorkerOptions(state, getters) {
            const allWorkOrders = state.workOrders;
            const activeWorkOrders = getters.filteredWorkOrders(["line_workers"]);
            if (!allWorkOrders || allWorkOrders.length === 0) return [];

            const activeLineWorkers = new Set(
                activeWorkOrders.flatMap((work_order) => work_order.line_workers.map((lw) => lw.name))
            );

            const lineWorkersMap = new Set();
            const activeLineWorkerOptions = [];
            const inactiveLineWorkerOptions = [];

            allWorkOrders.forEach((work_order) => {
                work_order.line_workers.forEach((lw) => {
                    if (!lw || !lw.name || lineWorkersMap.has(lw.name)) return;

                    lineWorkersMap.add(lw.name);
                    const isActive = activeLineWorkers.has(lw.name);
                    const lineWorkerOption = {
                        text: lw.name,
                        value: lw.name,
                        active: isActive,
                    };

                    if (isActive) {
                        activeLineWorkerOptions.push(lineWorkerOption);
                    } else {
                        inactiveLineWorkerOptions.push(lineWorkerOption);
                    }
                });
            });

            return [...activeLineWorkerOptions, ...inactiveLineWorkerOptions];
        },
        shiftOptions(state, getters) {
            const allWorkOrders = state.workOrders;
            const activeWorkOrders = getters.filteredWorkOrders(["shift"]);
            if (!allWorkOrders || allWorkOrders.length === 0) return [];

            const activeShifts = new Set(activeWorkOrders.map((work_order) => work_order.shift_type));

            const shiftMap = new Map();
            const activeShiftOptions = [];
            const inactiveShiftOptions = [];

            ["day", "night"].forEach((shift) => {
                if (shiftMap.has(shift)) return;

                const isActive = activeShifts.has(shift);
                const shiftOption = {
                    text: shift === "day" ? "Day shift" : "Night shift",
                    value: shift,
                    active: isActive,
                };

                if (isActive) {
                    activeShiftOptions.push(shiftOption);
                } else {
                    inactiveShiftOptions.push(shiftOption);
                }

                shiftMap.set(shift, shiftOption);

                if (shiftMap.size === 3) {
                    return [...activeShiftOptions, ...inactiveShiftOptions];
                }
            });

            return [...activeShiftOptions, ...inactiveShiftOptions];
        },
        productionProductTypeOptions(state, getters) {
            const allWorkOrders = state.workOrders;
            const activeWorkOrders = getters.filteredWorkOrders(["product_production_type"]);
            if (!allWorkOrders || allWorkOrders.length === 0) return [];

            const activeProductTypes = new Set(
                activeWorkOrders.map((work_order) => work_order.product_production_type)
            );
            const productTypes = [
                {value: "intermediate", text: "Intermediate product"},
                {value: "finished", text: "Finished product"},
            ];

            const activeProductTypeOptions = [];
            const inactiveProductTypeOptions = [];

            productTypes.forEach(({value, text}) => {
                const isActive = activeProductTypes.has(value);
                const productTypeOption = {text, value, active: isActive};

                if (isActive) {
                    activeProductTypeOptions.push(productTypeOption);
                } else {
                    inactiveProductTypeOptions.push(productTypeOption);
                }
            });

            return [...activeProductTypeOptions, ...inactiveProductTypeOptions];
        },

        xAxisValues(state) {
            const {start, end} = state.fetchDates;
            if (!start || !end) return [];
            const groupByWeek = state.groupByPeriod.includes("week");
            const groupByDay = state.groupByPeriod.includes("day");
            const startDate = new Date(start);
            const endDate = new Date(end);
            const dateArray = Array.from({length: (endDate - startDate) / 86400000 + 1}, (_, i) => {
                const d = new Date(startDate);
                d.setDate(d.getDate() + i);
                return d;
            });

            const uniquePeriods = new Set(
                dateArray.map((date) => {
                    const year = date.getFullYear();
                    if (groupByWeek) {
                        const tempDate = new Date(date);
                        tempDate.setUTCDate(tempDate.getUTCDate() + 4 - (tempDate.getUTCDay() || 7));
                        const firstDayOfYear = new Date(Date.UTC(tempDate.getUTCFullYear(), 0, 1));
                        const week = Math.ceil(((tempDate - firstDayOfYear) / 86400000 + 1) / 7);
                        return `${year}-W${String(week).padStart(2, "0")}`;
                    } else if(groupByDay) {
                        return `${year}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
                    } else {
                        return `${year}-${String(date.getMonth() + 1).padStart(2, "0")}`;
                    }
                })
            );

            return Array.from(uniquePeriods).sort();
        },
    },
};
